// Libs
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Grid,
    IconButton,
    ListItemText,
    MenuItem,
    Tooltip,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import axios from "src/utils/axios";

// Types
import { OfferTypeEnum } from "src/types/offer";
import type { MarketingActionVariantEnum } from "src/utils/variants";
import type { MultipleSelectFieldType } from "src/views/marketing-actions/marketing-actions-create-view/MarketingAction.utils";
import {
    type DiscountOption,
    isAllowCategorySelectionByServiceAndOfferType,
    isCheeckyTuesday,
} from "src/views/marketing-actions/marketing-actions-create-view/MarketingActionCreateView.logic";

// Utils
import { type FieldInputProps, useField } from "formik";
import { type TFunction, useTranslation } from "src/design-system/core/TranslationContext";
import { sliceString } from "src/utils/stringUtils";
import { getProductListQueryArgument } from "src/views/marketing-actions/components/form/hooks/useQueryProductList";
import {
    MARKETING_ACTIONS_CREATE_VIEW_TK,
    MARKETING_ACTIONS_TRANSLATION_NAMESPACE,
} from "src/views/marketing-actions/config";

// Hooks
import { useQueryClient } from "@tanstack/react-query";
import { orderBy, uniq } from "lodash";
import { BiSync } from "react-icons/bi";
import { useQueryPointsOfSales } from "src/api/pointsOfSales";
import { useAuth } from "src/hooks";

// Components
import type { Moment } from "moment";
import type { Item } from "src/types/item";
import type { ThirdServiceCategory } from "src/types/posCredential";
import { ItemBox } from "src/views/marketing-actions/components/Items/ItemBox";
import { CheeckyAlert } from "src/views/marketing-actions/components/form/CheeckyAlert";
import { OfferCharacteristicProductModal } from "src/views/marketing-actions/components/form/OfferCharacteristicCard/OfferCharacteristicProductModal";
import { OfferCharacteristicSharedInputStyles } from "src/views/marketing-actions/components/form/OfferCharacteristicCard/OfferCharcteristicCard.styles";
import { MultipleAutocompleteComponent } from "..";

interface OfferCharacteristicSharedInputProps {
    isSpecialOperation: boolean;
    recurrenceValue: {
        startDate: Moment;
        endDate: Moment;
        startTime: number;
        endTime: number;
        days: number[];
    };
    t: TFunction;
    serviceAndChannelField: FieldInputProps<any>;
    offerCharacteristicField: MultipleSelectFieldType;
    maxItems: number;
    productField: MultipleSelectFieldType;
    categoryItemListField: MultipleSelectFieldType;
    selectedChannel: any;
    selectedSource: string;
    selectedPosList: any;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    setFieldValueScenario?: (field: string, value: any, shouldValidate?: boolean) => void;
    discountField: MultipleSelectFieldType;
    cartField: MultipleSelectFieldType;
    isScenario: boolean;
    closeDialogForm?: () => void;
    productsFieldName: string;
    fieldName: string;
    variants: MarketingActionVariantEnum[];
    isCoFunded: boolean;
}

export const UNKNOWN_CATEGORY_NAME = "flynt-unknown";

/*
 *
 * @param productList: liste des produits ac un tableau de posIds qui indique sur quel pos le produit est disponible
 * @param isProduct: pour savoir si on veut compter ou avoir le produit
 * @returns une map par posId ac la liste des produits disponibles sur ce pos
 */

function formatPosListMap(productList: any[] = [], isProduct = false, posList = []) {
    const defaultPosList = orderBy(posList, ["name"]).reduce(
        (acc, e) => ({
            ...acc,
            [e.id]: isProduct ? [] : { numberOfProduct: 0, categoryItemList: [] },
        }),
        {}
    );

    const tmp = (productList ?? []).reduce((acc, product) => {
        return {
            ...acc,
            ...product.posIds?.reduce((currentAcc, posId) => {
                if (acc[posId] && isProduct) {
                    acc[posId].push(product);
                    return acc;
                } else if (isProduct) {
                    return {
                        ...currentAcc,
                        [posId]: acc[posId] ? acc[posId] : [product],
                    };
                } else {
                    return {
                        ...currentAcc,
                        [posId]: {
                            numberOfProduct: acc[posId]?.numberOfProduct
                                ? acc[posId]?.numberOfProduct + 1
                                : 1,
                            categoryItemList: acc[posId]?.categoryItemList
                                ? uniq([
                                      ...acc[posId].categoryItemList,
                                      product?.remoteCategory?.name,
                                  ])
                                : [product?.remoteCategory?.name],
                        },
                    };
                }
            }, {}),
        };
    }, defaultPosList);

    return tmp;
}

export const OfferCharacteristicSharedInput = ({
    isSpecialOperation,
    recurrenceValue,
    t,
    serviceAndChannelField,
    offerCharacteristicField,
    maxItems,
    productField,
    productsFieldName,
    categoryItemListField,
    selectedChannel,
    selectedSource,
    selectedPosList,
    setFieldValue,
    setFieldValueScenario,
    discountField,
    cartField,
    isScenario,
    fieldName,
    variants,
    isCoFunded,
}: OfferCharacteristicSharedInputProps): JSX.Element => {
    const clientQuery = useQueryClient();

    const { translateCommon } = useTranslation(
        `${MARKETING_ACTIONS_TRANSLATION_NAMESPACE}:${MARKETING_ACTIONS_CREATE_VIEW_TK}.steps.offer-type`
    );

    const [isLoading, setIsLoading] = useState<boolean>();

    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const { data: poss } = useQueryPointsOfSales({});
    const [field] = useField(fieldName);

    const isCategorySelection = isAllowCategorySelectionByServiceAndOfferType(
        serviceAndChannelField.value.service.value,
        field.value.value
    );

    const productOrCategories = isCategorySelection ? "categoryItemList" : "products";

    const [numberOfProductsByPos, setNumberOfProductsByPos] = useState<any>(
        formatPosListMap(field?.value[productOrCategories], false, selectedPosList)
    );

    const [itemListByPosId, setItemListByPosId] = useState<{ [key: number]: Item[] }>(
        formatPosListMap(field?.value[productOrCategories], true, selectedPosList)
    );

    const posLitQueryState = clientQuery.getQueryState(
        getProductListQueryArgument(selectedPosList, selectedSource, user).queryArg
    );

    const currentData = field?.value[productOrCategories]?.every((p) => p.posIds !== undefined);

    useEffect(() => {
        setNumberOfProductsByPos(
            formatPosListMap(field?.value[productOrCategories], false, selectedPosList)
        );
        setItemListByPosId(
            formatPosListMap(field?.value[productOrCategories], true, selectedPosList)
        );
    }, [
        currentData,
        posLitQueryState.fetchStatus === "fetching",
        field?.value[productOrCategories],
    ]);

    const classes = OfferCharacteristicSharedInputStyles();
    const paddingValue = isScenario ? 6 : 12;

    const [offerCharacteristicProductModal, setOfferCharacteristicProductModal] = useState(false);

    /**
     * ATTENTION VRAIMENT: Cette objet item a une référence partagé avec la liste d'objet de base de marketing action create form
     * DU COUP ! ne faut pas modifier l'objet ou push des trucs directement dans les enfants
     *
     * exemple bugué
     * item.posIds.push(posId);
     * exemple correcte
     * item.posIds = [...item.posIds, posId];
     *
     * @see https://www.github.com/goflynt/web/pull/2428
     */

    const items = clientQuery.getQueryData(
        getProductListQueryArgument(selectedPosList, selectedSource, user).queryArg
    ) as Item[];

    const currentArg = getProductListQueryArgument(selectedPosList, selectedSource, user);

    const handleClickSyncProduct = async () => {
        setIsLoading(true);
        try {
            for (let i = 0; i < selectedPosList.length; i += 5) {
                await axios.post(`/pos/remotechannelitem/update-many`, {
                    category: selectedChannel,
                    source: selectedSource,
                    posIds: selectedPosList.slice(i, i + 5).map((elm) => elm.id),
                });
            }
            enqueueSnackbar(t("sync.success"), {
                variant: "success",
            });
            setIsLoading(false);

            await clientQuery.refetchQueries({ queryKey: currentArg.queryArg });
        } catch (error) {
            if (error.data && error.status === 400) {
                const posNames = poss
                    .filter(
                        (pos) =>
                            error.data.invalidPosIds
                                .map((posId) => Number(posId))
                                .includes(pos.id) || Number(error.data.invalidPosLogin) === pos.id
                    )
                    .map((pos) => pos.name);

                enqueueSnackbar(
                    error.data.invalidPos
                        ? t("error.pos-wrong-credentials", { posName: posNames.join(", ") })
                        : t("error.pos-sync-error", { posName: posNames.join(", ") }),
                    {
                        variant: "error",
                    }
                );
            } else {
                enqueueSnackbar(translateCommon("errors.unknown-try-later"), {
                    variant: "error",
                });
            }
            setIsLoading(false);
        }
    };

    const orderedItemByCategory = useMemo(() => {
        const ordered = (items ?? []).sort((a, b) => {
            if (a.remoteCategory?.name === undefined) {
                return -1;
            }
            if (b.remoteCategory?.name === undefined) {
                return 1;
            }
            return a.remoteCategory?.name.localeCompare(b.remoteCategory?.name);
        });
        return ordered.map((elm) => {
            return {
                ...elm,
                name: elm.name,
                discountOption: +(elm as any).discountOption,
                remoteCategory: {
                    name: elm.remoteCategory?.name,
                },
                id: elm.id,
            };
        });
    }, [items]);

    const orderedCategoryByPos = useMemo(() => {
        const categoryItemList = items.reduce((acc, item) => {
            const categoryName = item.remoteCategory?.name ?? UNKNOWN_CATEGORY_NAME;
            const posIds = item.posIds;
            if (acc[categoryName] === undefined) {
                acc[categoryName] = {
                    // id: remoteCategoryId,
                    name: categoryName ?? UNKNOWN_CATEGORY_NAME,
                    posIds,
                    remoteCategory: item.remoteCategory ?? {
                        name: UNKNOWN_CATEGORY_NAME,
                    },
                    itemList: [item],
                    itemListString: item.name,
                };
            } else {
                const currentCategoryInAcc = acc[categoryName];
                posIds.forEach((posId) => {
                    if (currentCategoryInAcc.posIds.includes(posId) === false) {
                        currentCategoryInAcc.posIds = [...currentCategoryInAcc.posIds, posId];
                    }
                });
                currentCategoryInAcc.itemList.push(item);
                currentCategoryInAcc.itemListString = `${currentCategoryInAcc.itemListString}, ${item.name}`;
            }
            return acc;
        }, {});

        const orderedCategoryByPosArray = Object.keys(categoryItemList).map((key) => {
            return categoryItemList[key];
        });
        return orderedCategoryByPosArray;
    }, [items]);

    // transform orderedCategoryByPos object in array

    const displayItemList = [
        OfferTypeEnum.DiscountByItem,
        OfferTypeEnum.SpecificDish,
        OfferTypeEnum.FreeItem,
        OfferTypeEnum.Bogo,
    ];

    const updateGoodFormik = (value) => {
        // This is not the same setFieldValue in case of SS or classical offer
        if (isScenario === true) {
            setFieldValueScenario(fieldName, {
                ...value,
            });
        } else {
            setFieldValue(fieldName, {
                ...value,
            });
        }
    };

    const handleChangeProduct = (v) => {
        const newOfferCharacteristic = {
            ...field.value,
            products: v,
        };
        setNumberOfProductsByPos(formatPosListMap(v, false, selectedPosList));
        setItemListByPosId(formatPosListMap(v, true, selectedPosList));
        updateGoodFormik(newOfferCharacteristic);
    };

    const handleChangeCategoryItemList = (v) => {
        const newOfferCharacteristic = {
            ...field.value,
            categoryItemList: v,
        };
        setNumberOfProductsByPos(formatPosListMap(v, false, selectedPosList));
        setItemListByPosId(formatPosListMap(v, true, selectedPosList));
        updateGoodFormik(newOfferCharacteristic);
    };

    const onCancelProduct = (elm: any) => {
        const newProducts = field.value.products.filter((e) => e.id !== elm.id);
        const newOfferCharacteristic = {
            ...field.value,
            products: newProducts,
        };

        setNumberOfProductsByPos(formatPosListMap(newProducts, false, selectedPosList));
        setItemListByPosId(formatPosListMap(newProducts, true, selectedPosList));
        updateGoodFormik(newOfferCharacteristic);
    };

    const onCancelCategory = (elm: any) => {
        const newCategoryItemList = field.value.categoryItemList.filter((e) => e.name !== elm.name);
        const newOfferCharacteristic = {
            ...field.value,
            categoryItemList: newCategoryItemList,
        };

        setNumberOfProductsByPos(formatPosListMap(newCategoryItemList, false, selectedPosList));
        setItemListByPosId(formatPosListMap(newCategoryItemList, true, selectedPosList));
        updateGoodFormik(newOfferCharacteristic);
    };

    useEffect(() => {
        const tmp = isCheeckyTuesday({
            service: selectedSource as ThirdServiceCategory,
            offerType: field.value.value,
            startDate: recurrenceValue?.startDate,
            endDate: recurrenceValue?.endDate,
            startTime: recurrenceValue?.startTime,
            endTime: recurrenceValue?.endTime,
            days: recurrenceValue?.days,
            isNonFlynt: isCoFunded,
            countryCode: user?.organization?.countryCode,
        });
        updateGoodFormik({
            ...field.value,
            isCheeckyTuesday: tmp,
        });
    }, [
        selectedSource,
        field.value.value,
        recurrenceValue?.startDate,
        recurrenceValue?.endDate,
        recurrenceValue?.startTime,
        recurrenceValue?.endTime,
        recurrenceValue?.days,
        isCoFunded,
    ]);

    return (
        <>
            {/* SELECT OFFER TYPE */}
            <Grid item md={12} xs={paddingValue}>
                <Box
                    paddingLeft={isScenario ? 0 : 2}
                    paddingRight={isScenario ? 0 : 2}
                    paddingTop={isScenario ? 0 : 2}
                    paddingBottom={2}
                >
                    <MultipleAutocompleteComponent
                        key={offerCharacteristicField.name}
                        multiple={false}
                        disabled={
                            (!serviceAndChannelField.value.channel.value && !isSpecialOperation) ||
                            (isSpecialOperation && Boolean(offerCharacteristicField.name))
                        }
                        name={offerCharacteristicField.name}
                        handleChange={offerCharacteristicField.autoCompleteProps.handleChange}
                        inputProps={offerCharacteristicField.autoCompleteProps.inputProps}
                        options={offerCharacteristicField.autoCompleteProps.options}
                    />
                </Box>
            </Grid>

            {/* SELECT PRODUCT CLASSIQUE */}
            {maxItems > 0 && isCategorySelection === false && (
                <Grid item md={12} xs={paddingValue}>
                    <Box
                        paddingLeft={isScenario ? 0 : 2}
                        paddingRight={isScenario ? 0 : 2}
                        paddingTop={isScenario ? 0 : 2}
                        paddingBottom={2}
                    >
                        <Box display="flex" alignItems="flex-end" width={"100%"}>
                            <MultipleAutocompleteComponent
                                renderTags={() => null}
                                getOptionSelected={(option, value) => option.id === value.id}
                                getOptionDisabled={(optionT) => {
                                    const option = items.find(
                                        (elm) =>
                                            `${elm.name}-${elm.remoteCategory?.name}` === optionT.id
                                    );
                                    const isSomePosAreNotAvailable = (option as any)?.posIds.some(
                                        (e) => numberOfProductsByPos[e]?.numberOfProduct >= maxItems
                                    );
                                    const isAllPosAreNotAvailable = (option as any)?.posIds.every(
                                        (e) => numberOfProductsByPos[e]?.numberOfProduct >= maxItems
                                    );
                                    const currentCheck =
                                        field.value.products.findIndex((e) => e.id === optionT.id) >
                                        -1;

                                    return (
                                        (isSomePosAreNotAvailable && !currentCheck) ||
                                        (isAllPosAreNotAvailable && !currentCheck)
                                    );
                                }}
                                fullWidth
                                disableCloseOnSelect
                                multiple
                                name={productField.name}
                                inputProps={productField.autoCompleteProps.inputProps}
                                handleChange={handleChangeProduct}
                                options={orderedItemByCategory}
                                renderOption={(props, optionT) => {
                                    const option = items.find(
                                        (elm) =>
                                            `${elm.name}-${elm.remoteCategory?.name}` === optionT.id
                                    );
                                    const currentPosList = selectedPosList.filter((elm) => {
                                        return (
                                            (option as any)?.posIds?.includes(elm.id) &&
                                            numberOfProductsByPos[elm.id]?.numberOfProduct >=
                                                maxItems
                                        );
                                    });
                                    const secondaryText =
                                        selectedPosList.length !== 1
                                            ? (option as any)?.posIds?.length ===
                                              selectedPosList.length
                                                ? t("category.maxPosList")
                                                : t("category.posList", {
                                                      posDispo: (option as any)?.posIds?.length,
                                                      maxPos: selectedPosList.length,
                                                  })
                                            : null;
                                    const isSomePosAreNotAvailable = (option as any)?.posIds.some(
                                        (e) => numberOfProductsByPos[e]?.numberOfProduct >= maxItems
                                    );
                                    const isAllPosAreNotAvailable = (option as any)?.posIds.every(
                                        (e) => numberOfProductsByPos[e]?.numberOfProduct >= maxItems
                                    );
                                    const currentCheck =
                                        field.value.products.findIndex((e) => e.id === optionT.id) >
                                        -1;

                                    return (
                                        <Box
                                            display={"flex"}
                                            width={"100%"}
                                            {...props}
                                            key={option?.id}
                                        >
                                            <MenuItem
                                                sx={{
                                                    width: "100%",
                                                    "&:hover": {
                                                        backgroundColor: "transparent",
                                                    },
                                                    "&:active": {
                                                        backgroundColor: "transparent",
                                                    },
                                                }}
                                                key={option?.id}
                                                dense={false}
                                                disableGutters={true}
                                                disableRipple={true}
                                            >
                                                <Checkbox
                                                    checked={currentCheck}
                                                    disabled={
                                                        isSomePosAreNotAvailable && !currentCheck
                                                    }
                                                />
                                                <Box
                                                    display={"flex"}
                                                    alignItems={"center"}
                                                    width={"100%"}
                                                >
                                                    <ListItemText
                                                        primary={option?.name}
                                                        secondary={secondaryText}
                                                        disabled={
                                                            isSomePosAreNotAvailable &&
                                                            !currentCheck
                                                        }
                                                    />
                                                    {isSomePosAreNotAvailable &&
                                                        !isAllPosAreNotAvailable &&
                                                        !currentCheck && (
                                                            <Tooltip
                                                                style={{
                                                                    pointerEvents: "all",
                                                                }}
                                                                title={
                                                                    <Box
                                                                        className={classes.tooltip}
                                                                    >
                                                                        <span>
                                                                            {t(
                                                                                "category.maxOnePos"
                                                                            )}
                                                                        </span>
                                                                        <ul>
                                                                            {currentPosList.map(
                                                                                (e) => {
                                                                                    return (
                                                                                        <li
                                                                                            key={
                                                                                                e.name
                                                                                            }
                                                                                        >
                                                                                            {e.name}
                                                                                        </li>
                                                                                    );
                                                                                }
                                                                            )}
                                                                        </ul>
                                                                    </Box>
                                                                }
                                                            >
                                                                <ReportProblemOutlinedIcon
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                </Box>
                                            </MenuItem>
                                        </Box>
                                    );
                                }}
                                groupBy={(option: any) => {
                                    if (
                                        option?.remoteCategory?.name === undefined ||
                                        option?.remoteCategory?.name === UNKNOWN_CATEGORY_NAME
                                    ) {
                                        return t("category.general");
                                    }
                                    return option?.remoteCategory?.name;
                                }}
                            />
                            {!isLoading ? (
                                <Tooltip title={t("refresh-products-tooltip")}>
                                    <IconButton onClick={() => handleClickSyncProduct()}>
                                        <BiSync />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Box p={2}>
                                    <CircularProgress size={20} />
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Grid>
            )}

            {/* SELECT CATEGORY PRODUCT LIST ITEM  */}
            {maxItems > 0 && isCategorySelection === true && (
                <Grid item md={12} xs={paddingValue}>
                    <Box
                        paddingLeft={isScenario ? 0 : 2}
                        paddingRight={isScenario ? 0 : 2}
                        paddingTop={isScenario ? 0 : 2}
                        paddingBottom={2}
                    >
                        <Box display="flex" alignItems="flex-end" width={"100%"}>
                            <MultipleAutocompleteComponent
                                renderTags={() => null}
                                getOptionSelected={(option, value) => {
                                    return option.name === value.name;
                                }}
                                getOptionDisabled={(optionT: any) => {
                                    const currentCheck =
                                        field.value.categoryItemList?.findIndex(
                                            (e) => e.name === optionT.name
                                        ) > -1;

                                    const isAlreadyBind =
                                        optionT.posIds.findIndex(
                                            (e) =>
                                                numberOfProductsByPos[e]?.categoryItemList
                                                    ?.length ?? 0 > 0
                                        ) !== -1;
                                    return isAlreadyBind && !currentCheck;
                                }}
                                fullWidth
                                disableCloseOnSelect
                                multiple
                                name={categoryItemListField.name}
                                inputProps={categoryItemListField.autoCompleteProps.inputProps}
                                handleChange={handleChangeCategoryItemList}
                                options={orderedCategoryByPos}
                                renderOption={(props: any, optionT: any) => {
                                    const currentPosList = selectedPosList.filter((elm) => {
                                        return optionT.posIds?.includes(elm.id);
                                    });
                                    const nameText =
                                        optionT.remoteCategory?.name ?? UNKNOWN_CATEGORY_NAME;

                                    const availableNumberText =
                                        selectedPosList.length !== 1
                                            ? optionT.posIds?.length === selectedPosList.length
                                                ? `- ${t("category.maxPosList")}`
                                                : `- ${t("category.posList", {
                                                      posDispo: optionT.posIds?.length,
                                                      maxPos: selectedPosList.length,
                                                  })}`
                                            : "";

                                    const text = `${nameText} ${availableNumberText}`;
                                    const secondaryText = optionT.itemListString;
                                    const currentCheck =
                                        field.value.categoryItemList?.findIndex(
                                            (e) => e.name === optionT.name
                                        ) > -1;
                                    const isSomePosAreNotAvailable = optionT?.posIds?.some(
                                        (e) => numberOfProductsByPos[e]?.categoryItemList === e
                                    );
                                    const isAlreadyBind =
                                        optionT?.posIds?.findIndex(
                                            (e) =>
                                                numberOfProductsByPos[e]?.categoryItemList
                                                    ?.length ?? 0 > 0
                                        ) !== -1;

                                    const { key, ...optionProps } = props;
                                    return (
                                        <MenuItem
                                            sx={{
                                                width: "100%",
                                                "&:hover": {
                                                    backgroundColor: "transparent",
                                                },
                                                "&:active": {
                                                    backgroundColor: "transparent",
                                                },
                                            }}
                                            key={key}
                                            {...optionProps}
                                            dense={false}
                                            disableGutters={true}
                                            disableRipple={true}
                                        >
                                            <Checkbox
                                                checked={currentCheck}
                                                disabled={isSomePosAreNotAvailable && !currentCheck}
                                            />
                                            <Box
                                                display={"flex"}
                                                alignItems={"center"}
                                                width={"100%"}
                                            >
                                                <ListItemText
                                                    primary={text}
                                                    secondaryTypographyProps={{
                                                        style: {
                                                            fontSize: 11,
                                                            color: "#7A85A2",
                                                            whiteSpace: "initial",
                                                        },
                                                    }}
                                                    secondary={secondaryText}
                                                    disabled={
                                                        isSomePosAreNotAvailable && !currentCheck
                                                    }
                                                />
                                                {isAlreadyBind && !currentCheck && (
                                                    <Tooltip
                                                        style={{
                                                            pointerEvents: "all",
                                                        }}
                                                        title={
                                                            <Box className={classes.tooltip}>
                                                                <span>
                                                                    {t("category.maxCategories")}
                                                                </span>
                                                                <ul>
                                                                    {currentPosList.map((e) => {
                                                                        return (
                                                                            <li key={e.name}>
                                                                                {e.name}
                                                                            </li>
                                                                        );
                                                                    })}
                                                                </ul>
                                                            </Box>
                                                        }
                                                    >
                                                        <ReportProblemOutlinedIcon
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </MenuItem>
                                    );
                                }}
                            />
                            {!isLoading ? (
                                <Tooltip title={t("refresh-products-tooltip")}>
                                    <IconButton onClick={() => handleClickSyncProduct()}>
                                        <BiSync />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Box p={2}>
                                    <CircularProgress size={20} />
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Grid>
            )}

            {field.value && !(posLitQueryState.fetchStatus === "fetching") && (
                <>
                    {/* PRODUITS CLASSIQUES  */}
                    {displayItemList?.includes(field.value.value) &&
                        field?.value?.products?.length > 0 && (
                            <Grid item md={12} xs={paddingValue}>
                                {selectedPosList.length !== 1 && (
                                    <Box
                                        width={"100%"}
                                        paddingBottom={"8px"}
                                        display={"flex"}
                                        justifyContent={"center"}
                                    >
                                        <Button
                                            color="primary"
                                            disabled={false}
                                            variant="outlined"
                                            fullWidth
                                            onClick={() => {
                                                setOfferCharacteristicProductModal(
                                                    !offerCharacteristicProductModal
                                                );
                                            }}
                                        >
                                            {t("category.repartition")}
                                        </Button>
                                    </Box>
                                )}
                                {field.value.products?.map((elm, index) => {
                                    let itemCategoryName =
                                        elm.categoryName ?? elm.remoteCategory?.name;
                                    if (itemCategoryName === undefined) {
                                        itemCategoryName = UNKNOWN_CATEGORY_NAME;
                                    }

                                    const isDisplaySelector =
                                        field.value.value === OfferTypeEnum.DiscountByItem;
                                    return (
                                        <Box
                                            width={"100%"}
                                            paddingBottom={"8px"}
                                            key={`${index}-item-box`}
                                        >
                                            {/* TODO REMOVE PADDING AND WIDTH FROM HERE */}
                                            <ItemBox
                                                isDisplaySelector={isDisplaySelector}
                                                categoryName={
                                                    itemCategoryName === UNKNOWN_CATEGORY_NAME
                                                        ? t("category.general")
                                                        : itemCategoryName
                                                }
                                                itemName={elm.name}
                                                secondaryText={
                                                    selectedPosList.length !== 1
                                                        ? elm.posIds?.length ===
                                                          selectedPosList.length
                                                            ? t("category.allPos")
                                                            : `${elm.posIds?.length} / ${selectedPosList.length}`
                                                        : null
                                                }
                                                name={`${productsFieldName}[${index}].discountOption`}
                                                discountField={discountField}
                                                onCancel={() => onCancelProduct(elm)}
                                            />
                                        </Box>
                                    );
                                })}
                            </Grid>
                        )}

                    {/* SELECTION CATEGORIES */}
                    {displayItemList?.includes(field.value.value) &&
                        field?.value?.categoryItemList?.length > 0 && (
                            <Grid item md={12} xs={paddingValue}>
                                {selectedPosList.length !== 1 && (
                                    <Box
                                        width={"100%"}
                                        paddingBottom={"8px"}
                                        display={"flex"}
                                        justifyContent={"center"}
                                    >
                                        <Button
                                            color="primary"
                                            disabled={false}
                                            variant="outlined"
                                            fullWidth
                                            onClick={() => {
                                                setOfferCharacteristicProductModal(
                                                    !offerCharacteristicProductModal
                                                );
                                            }}
                                        >
                                            {t("category.repartition-categories")}
                                        </Button>
                                    </Box>
                                )}
                                {field.value.categoryItemList?.map((elm, index) => {
                                    let itemCategoryName =
                                        elm.categoryName ?? elm.remoteCategory?.name;
                                    if (itemCategoryName === undefined) {
                                        itemCategoryName = UNKNOWN_CATEGORY_NAME;
                                    }
                                    return (
                                        <Box
                                            width={"100%"}
                                            paddingBottom={"8px"}
                                            key={`${index}-item-box`}
                                        >
                                            {/* TODO REMOVE PADDING AND WIDTH FROM HERE */}
                                            <ItemBox
                                                isDisplaySelector={false}
                                                categoryName={
                                                    itemCategoryName === UNKNOWN_CATEGORY_NAME
                                                        ? t("category.general")
                                                        : sliceString(itemCategoryName, 25)
                                                }
                                                itemName={sliceString(elm.itemListString, 25)}
                                                secondaryText={
                                                    selectedPosList.length !== 1
                                                        ? elm.posIds?.length ===
                                                          selectedPosList.length
                                                            ? t("category.allPos")
                                                            : `${elm.posIds?.length} / ${selectedPosList.length}`
                                                        : null
                                                }
                                                name={`${categoryItemListField.name}[${index}].discountOption`}
                                                discountField={discountField}
                                                onCancel={() => onCancelCategory(elm)}
                                            />
                                        </Box>
                                    );
                                })}
                            </Grid>
                        )}

                    {/* SELECT DISCOUNT */}
                    {field.value.value !== OfferTypeEnum.DiscountByItem && (
                        <Grid item md={6} xs={paddingValue}>
                            <Box
                                paddingLeft={isScenario ? 0 : 2}
                                paddingRight={isScenario ? 0 : 2}
                                paddingTop={isScenario ? 0 : 2}
                                paddingBottom={2}
                            >
                                <MultipleAutocompleteComponent
                                    key={discountField.name}
                                    multiple={false}
                                    disabled={
                                        (!discountField.autoCompleteProps.options.length &&
                                            !isSpecialOperation) ||
                                        discountField.autoCompleteProps.disabled
                                    }
                                    name={discountField.name}
                                    handleChange={discountField.autoCompleteProps.handleChange}
                                    inputProps={discountField.autoCompleteProps.inputProps}
                                    options={discountField.autoCompleteProps.options}
                                    groupBy={
                                        uniq(
                                            discountField.autoCompleteProps.options?.map(
                                                (option: DiscountOption) => option.typeDiscount
                                            )
                                        )?.length > 1
                                            ? (option: DiscountOption) => {
                                                  return translateCommon(
                                                      `enums.discount-type.${option.typeDiscount}`
                                                  );
                                              }
                                            : undefined
                                    }
                                />
                            </Box>
                        </Grid>
                    )}

                    {/* SELECT CART */}
                    {field.value.value !== OfferTypeEnum.DiscountByItem && (
                        <Grid item md={6} xs={paddingValue}>
                            <Box
                                paddingLeft={isScenario ? 0 : 2}
                                paddingRight={isScenario ? 0 : 2}
                                paddingTop={isScenario ? 0 : 2}
                                paddingBottom={2}
                            >
                                <MultipleAutocompleteComponent
                                    key={cartField.name}
                                    multiple={false}
                                    name={cartField.name}
                                    disabled={
                                        !cartField.autoCompleteProps.options.length ||
                                        (isScenario !== true &&
                                            Boolean(cartField.autoCompleteProps.disabled))
                                    }
                                    handleChange={cartField.autoCompleteProps.handleChange}
                                    inputProps={cartField.autoCompleteProps.inputProps}
                                    options={cartField.autoCompleteProps.options}
                                />
                            </Box>
                        </Grid>
                    )}

                    <OfferCharacteristicProductModal
                        isCategorySelection={isCategorySelection}
                        isOpen={offerCharacteristicProductModal}
                        onClose={setOfferCharacteristicProductModal}
                        itemListByPos={itemListByPosId}
                        numberOfProductsByPos={numberOfProductsByPos}
                        posList={selectedPosList}
                        numberOfItems={maxItems}
                        onConfirm={() => {
                            setOfferCharacteristicProductModal(!offerCharacteristicProductModal);
                        }}
                    />
                </>
            )}

            {field?.value?.isCheeckyTuesday && (
                <CheeckyAlert isScenario={isScenario} variantList={variants} />
            )}
        </>
    );
};
